import { computed } from 'vue'

import type {
  BulkActionResponse,
  BulkFieldValue,
  BulkFieldValues,
  BulkId
} from '@/composables/use-bulk-operations/types'
import type { SalesLedgerScope } from '@/domains/products/components/organisms/twn-sales-ledger/TwnSalesLedger.types.ts'
import type { ProductModel } from '@/domains/products/types/models/ProductModel.types.ts'
import type ProductFormOptions from '@/domains/products/types/ProductFormOptions.types.ts'
import type {
  AddInternalBarcodePayload,
  IssueInternalBarcodesPayload,
  ListVariantLabelLinesPayload
} from '@/domains/products/types/requests/InternalBarcodeRequests.types.ts'
import type {
  FindProductRequest,
  GetProductRequest,
  IndexProductRequest,
  ProductEnhanceRequest,
  ProductEnhanceResponse
} from '@/domains/products/types/requests/ProductRequests.types'
import type { RepriceVariantPayload } from '@/domains/products/types/requests/RepriceVariantRequest.types.ts'
import type {
  ClassifyInternalCodesResponse,
  InternalCodeLookalikesResponse,
  IssuedInternalBarcode
} from '@/domains/products/types/responses/InternalBarcodeResponses.types.ts'
import type { IndexProductResponse } from '@/domains/products/types/responses/ProductResponses.types.ts'
import type { LabelLineRow } from '@/domains/shelf-labels/types/models/LabelLineRow.types.ts'
import type { FetchClientOptionsPayload } from '@/types/common/models/FetchClientOptionsPayload.ts'
import type { QueryParams } from '@/utils/url-util/urlUtil.ts'

import { callAnalytics } from '@/domains/stats/services/analytics'
import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil.ts'

/**
 * What the sales ledger asks the report for.
 *
 * ⚠ **`order_item.id` is what makes it a ledger.** Without it the report groups, and two
 * variants of one family collapse into a single row — `order_item.item_name` resolves to
 * `products.title` for both.
 *
 * ⚠ **Per page is a closed set: 10, 20, 50 or 100.** `PaginationOptions` falls back to 20 for
 * anything else, so 25 reads as a working request and quietly returns 20.
 */
const SALES_LEDGER_COLUMNS = [
  'order_item.id',
  'order_item.item_id',
  'order_item.created_at',
  'order_item.quantity',
  'order_item.unit_price',
  'order_item.total_amount'
] as const

const callProducts = () => {
  const index = (payload: IndexProductRequest) => {
    return twineServiceClient.get<IndexProductResponse, IndexProductRequest>({
      endpoint: relativeZiggyRoute('admin.products.index'),
      queryParams: {
        ...payload.queryParams
      } as QueryParams<IndexProductRequest>,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /** One product by id, or null once it is gone. `show` binds by slug, so this reads the index's `id` filter. */
  const findById = async (payload: FindProductRequest) => {
    const response = await index({ queryParams: { id: payload.id, per_page: 1 } })

    return { ...response, data: computed(() => response.data.value?.data[0] ?? null) }
  }

  const show = (payload: GetProductRequest & { preventConcurrent?: boolean; token?: string }) => {
    return twineServiceClient.get<ProductModel, GetProductRequest>({
      endpoint: relativeZiggyRoute('admin.products.show', { product: payload.slug }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const enhance = (payload: ProductEnhanceRequest & { preventConcurrent?: boolean; token?: string }) => {
    return twineServiceClient.post<ProductEnhanceResponse, ProductEnhanceRequest>({
      endpoint: relativeZiggyRoute('admin.products.enhance'),
      body: payload,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * The option sets the product form needs — VAT rates, measurement units, variant
   * types and the rest.
   *
   * The Inertia product pages receive these as page props. Anything rendering the
   * form outside a page, such as the product slide-over on a bill, has no page
   * props to read and fetches them here instead.
   */
  const formOptions = () => {
    return twineServiceClient.get<ProductFormOptions>({
      endpoint: relativeZiggyRoute('admin.products.form-options')
    })
  }

  /**
   * ⚠ AJAX only. `admin.products.store` answers a redirect to an Inertia request
   * and the created product to this one, so posting through Inertia here would
   * navigate away from whatever opened the slide-over.
   */
  const store = (payload: { body: ProductModel }) => {
    return twineServiceClient.post<ProductModel, ProductModel>({
      endpoint: relativeZiggyRoute('admin.products.store'),
      body: payload.body
    })
  }

  const update = (payload: { slug: string; body: ProductModel }) => {
    return twineServiceClient.put<ProductModel, ProductModel>({
      endpoint: relativeZiggyRoute('admin.products.update', { product: payload.slug }),
      body: payload.body
    })
  }

  /**
   * Every paid line of one product's variants, or of one variant, newest first.
   *
   * Named for the question rather than the endpoint, and composed over
   * `callAnalytics().dynamicReport` so the dynamic-report transport has one definition.
   *
   * ⚠ **`windowStartsOn` must hand this `d-m-Y`.** `OrderItemQueryProvider` declares its date
   * filter with `format('d-m-Y')` and `DateFilter::parse` then runs `createFromFormat` against
   * it, so an ISO value is a 500 rather than an ignored filter.
   */
  const salesLedger = (payload: { scope: SalesLedgerScope; since: string; perPage: number }) =>
    callAnalytics().dynamicReport({
      queryParams: {
        model: 'order_item',
        columns: [...SALES_LEDGER_COLUMNS],
        sort: '-order_item.created_at',
        /**
         * ⚠ **The scope's `type` is the backend filter key, not just a label.** `product`,
         * `variant` and `menu_item` are `ProductFilter`, `VariantFilter` and `MenuItemFilter`'s
         * declared keys, and an undeclared key applies nothing rather than erroring — a rename
         * on either side would render the whole tenant's sales on one card. Pinned by
         * `ProductAndVariantFilterTest`.
         */
        [`filter[${payload.scope.type}]`]: String(payload.scope.id),
        'filter[created_at][gte]': payload.since,
        'pagination[page]': '1',
        'pagination[per_page]': String(payload.perPage)
      }
    })

  /**
   * Gives one variant a further internal barcode, whatever it holds already, and answers the stored code.
   * Backend: AddInternalBarcodeToVariantController
   */
  const addInternalBarcode = (payload: AddInternalBarcodePayload) => {
    return twineServiceClient.post<IssuedInternalBarcode>({
      endpoint: relativeZiggyRoute('admin.products.variants.add-internal-barcode', { variant: payload.variantId }),
      body: {},
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Records a cost and moves the selling price beside it, as a bill line does. Answers nothing.
   * Backend: RepriceVariantController
   */
  const repriceVariant = (payload: RepriceVariantPayload) => {
    return twineServiceClient.post<void, RepriceVariantPayload['body']>({
      endpoint: relativeZiggyRoute('admin.products.variants.reprice', { variant: payload.variantId }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Issues one internal barcode per variant that has none, and answers each variant's code.
   * Backend: IssueInternalBarcodesController
   */
  const issueInternalBarcodes = (payload: IssueInternalBarcodesPayload) => {
    return twineServiceClient.post<IssuedInternalBarcode[], IssueInternalBarcodesPayload['body']>({
      endpoint: relativeZiggyRoute('admin.products.variants.internal-barcodes.store'),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * The label rows the print slide-over lists for the given variants. The tenant's default
   * template answers when none is named.
   */
  const listVariantLabelLines = (payload: ListVariantLabelLinesPayload) => {
    return twineServiceClient.get<LabelLineRow[]>({
      endpoint: relativeZiggyRoute('admin.products.variants.label-lines.index'),
      queryParams: payload.queryParams,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Names each code classifying would change, and each it leaves because another row holds it.
   * GET /admin/settings/barcodes/internal-codes/lookalikes
   */
  const listInternalCodeLookalikes = (payload?: FetchClientOptionsPayload) => {
    return twineServiceClient.get<InternalCodeLookalikesResponse>({
      endpoint: relativeZiggyRoute('admin.settings.barcodes.internal-codes.lookalikes'),
      preventConcurrent: payload?.preventConcurrent,
      token: payload?.token
    })
  }

  /**
   * Classifies the existing internal codes and answers how many changed.
   * POST /admin/settings/barcodes/internal-codes/classify
   */
  const classifyInternalCodes = (payload?: FetchClientOptionsPayload) => {
    return twineServiceClient.post<ClassifyInternalCodesResponse>({
      endpoint: relativeZiggyRoute('admin.settings.barcodes.internal-codes.classify'),
      body: undefined,
      preventConcurrent: payload?.preventConcurrent,
      token: payload?.token
    })
  }

  // Bulk calls act on the rows ticked on the current page, named by id.
  const bulkUpdate = async (ids: BulkId[], changes: BulkFieldValues): Promise<BulkActionResponse> => {
    const { data } = await twineServiceClient.patch<BulkActionResponse, Record<string, BulkFieldValue | BulkId[]>>({
      endpoint: relativeZiggyRoute('admin.products.bulkUpdate'),
      body: { ids, ...changes }
    })

    return data.value as BulkActionResponse
  }

  const bulkDelete = async (ids: BulkId[]): Promise<BulkActionResponse> => {
    const { data } = await twineServiceClient.delete<BulkActionResponse, never, { ids: BulkId[] }>({
      endpoint: relativeZiggyRoute('admin.products.bulkDelete'),
      body: { ids }
    })

    return data.value as BulkActionResponse
  }

  return {
    index,
    enhance,
    formOptions,
    salesLedger,
    show,
    findById,
    store,
    update,
    bulkUpdate,
    bulkDelete,
    issueInternalBarcodes,
    addInternalBarcode,
    repriceVariant,
    listVariantLabelLines,
    classifyInternalCodes,
    listInternalCodeLookalikes
  }
}

export { callProducts }
